import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Heart, Sparkles, Calendar, ChevronRight, Baby, ShieldAlert,
  Activity, BookOpen, TrendingUp, Sun, Apple, Clock, AlertTriangle,
} from 'lucide-react';
import { api, STATUS_PAL, relTime } from './api';
import MaternalSetup from './MaternalSetup';
import { MaternalLoader } from './_ui';
import { useSafeBack } from '@/utils/useSafeBack';

const stageColor = (stage) => {
  if (stage === 't1') return { from: 'from-pink-500', to: 'to-rose-500', soft: 'bg-pink-50' };
  if (stage === 't2') return { from: 'from-rose-500', to: 'to-pink-500', soft: 'bg-rose-50' };
  if (stage === 't3') return { from: 'from-fuchsia-500', to: 'to-rose-500', soft: 'bg-fuchsia-50' };
  if (stage === 'postpartum') return { from: 'from-purple-500', to: 'to-fuchsia-500', soft: 'bg-purple-50' };
  if (stage === 'recovery') return { from: 'from-violet-500', to: 'to-purple-500', soft: 'bg-violet-50' };
  return { from: 'from-rose-500', to: 'to-pink-500', soft: 'bg-rose-50' };
};

const MaternalHub = () => {
  const navigate = useNavigate();
  const handleBack = useSafeBack('/family-health');
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(
    () => api.snapshot().then(setSnap).catch(() => setSnap({ has_profile: false })),
    [],
  );
  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  if (loading) return <MaternalLoader label="Loading your maternal companion…" testid="maternal-hub-loading" />;
  if (!snap || !snap.has_profile) return <MaternalSetup onSaved={() => { setLoading(true); refresh().finally(() => setLoading(false)); }} />;

  const { stage_info, status, last_checkin_at, last_checkin_score, next_appointment, week_card, postpartum_card, focus, reassurance, recent_checkins } = snap;
  const sp = STATUS_PAL[status] || STATUS_PAL['Not checked yet'];
  const pal = stageColor(stage_info?.stage);
  const isPregnant = ['t1', 't2', 't3'].includes(stage_info?.stage);
  const isPP = ['postpartum', 'recovery'].includes(stage_info?.stage);

  // Calm, non-gamified freshness signal per module. Returns null when there's
  // nothing meaningful to show — we'd rather show nothing than a meaningless
  // "Updated never" string.
  const isToday = (iso) => {
    if (!iso) return false;
    const a = new Date(iso); const b = new Date();
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };
  const isYesterday = (iso) => {
    if (!iso) return false;
    const a = new Date(iso); const b = new Date(); b.setDate(b.getDate() - 1);
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };
  // Soft "days-in-a-row" signal — counts consecutive days backwards from today
  // in the recent_checkins list. Plain number in, gentle copy out.
  const checkinStreak = () => {
    if (!Array.isArray(recent_checkins) || recent_checkins.length === 0) return 0;
    const seenDays = new Set();
    for (const r of recent_checkins) {
      const d = new Date(r.created_at);
      seenDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    }
    let streak = 0;
    const cursor = new Date();
    while (true) {
      const k = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
      if (seenDays.has(k)) { streak += 1; cursor.setDate(cursor.getDate() - 1); }
      else break;
    }
    return streak;
  };
  const streak = checkinStreak();
  const moduleMeta = (key) => {
    if (key === 'checkin') {
      if (isToday(last_checkin_at)) {
        if (streak >= 3) return { label: `Checked in today · ${streak}-day rhythm`, tone: 'fresh' };
        return { label: 'Checked in today', tone: 'fresh' };
      }
      if (isYesterday(last_checkin_at)) return { label: 'Last checked in yesterday', tone: 'soft' };
      if (last_checkin_at) return { label: `Last checked in ${relTime(last_checkin_at)}`, tone: 'soft' };
      return { label: 'A gentle minute today', tone: 'soft' };
    }
    if (key === 'symptoms') {
      // Symptoms are recorded inside check-ins, so reuse last_checkin_at.
      if (isToday(last_checkin_at)) return { label: 'Updated today', tone: 'fresh' };
      if (last_checkin_at)         return { label: `Updated ${relTime(last_checkin_at)}`, tone: 'soft' };
      return null;
    }
    if (key === 'week')      return week_card ? { label: 'Refreshed this week', tone: 'soft' } : null;
    if (key === 'nutrition') return week_card ? { label: 'Tuned to your week', tone: 'soft' } : null;
    if (key === 'appts') {
      if (next_appointment?.date) {
        const d = new Date(next_appointment.date + 'T00:00:00');
        const ms = d.getTime() - new Date(new Date().toDateString()).getTime();
        const days = Math.round(ms / 86400000);
        if (days === 0) return { label: 'A visit today', tone: 'fresh' };
        if (days === 1) return { label: 'Visit tomorrow', tone: 'fresh' };
        if (days > 0)   return { label: `Next visit in ${days} days`, tone: 'soft' };
      }
      return { label: 'Plan a visit', tone: 'soft' };
    }
    if (key === 'postpartum') return { label: 'Updated when you log', tone: 'soft' };
    if (key === 'trends') {
      const n = (recent_checkins || []).length;
      if (n >= 2) return { label: `Based on your last ${Math.min(n, 7)} day${n === 1 ? '' : 's'}`, tone: 'soft' };
      return null;
    }
    return null;
  };

  const modules = [
    { key: 'checkin',  icon: Heart,    title: 'Daily check-in',     desc: 'Mood, energy, symptoms, sleep — under 10 seconds.', cta: 'Check in now',         route: '/maternal-health/checkin', show: true },
    { key: 'symptoms', icon: ShieldAlert, title: 'Symptom tracker',  desc: 'See which symptoms are common vs need care.',     cta: 'Log symptoms',         route: '/maternal-health/symptoms', show: true },
    { key: 'week',     icon: BookOpen, title: 'This week',           desc: 'Baby development, what to focus on, red flags.',  cta: 'View week insight',    route: '/maternal-health/week', show: isPregnant },
    { key: 'appts',    icon: Calendar, title: 'Appointments',        desc: 'Upcoming visits + what to prepare.',              cta: 'Manage schedule',      route: '/maternal-health/appointments', show: true },
    { key: 'nutrition',icon: Apple,    title: 'Nutrition focus',     desc: 'Trimester-aware daily nutrient & avoid alerts.',  cta: "Today's nutrition",    route: '/maternal-health/nutrition', show: isPregnant },
    { key: 'postpartum', icon: Baby,   title: 'Postpartum recovery', desc: 'Bleeding, mood, feeding, sleep, pain — supported.',cta: 'Track recovery',      route: '/maternal-health/postpartum', show: isPP },
    { key: 'trends',   icon: TrendingUp, title: 'Trends',            desc: 'Mood, energy, sleep across the last 7 days.',     cta: 'See trends',           route: '/maternal-health/trends', show: true },
  ].filter((m) => m.show);

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-hub">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 hl-stagger">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Family Health
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${pal.from} ${pal.to} flex items-center justify-center shrink-0`}>
              <Heart className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Maternal Health</h1>
              <p className="text-xs text-gray-500 mt-0.5">A daily companion through pregnancy, postpartum, and recovery.</p>
            </div>
          </div>
        </header>

        {/* TODAY'S MATERNAL FOCUS — the priority block */}
        <section className={`rounded-2xl border-2 ${sp.border} ${sp.bg} p-5 mb-4 hl-fade-up`} data-testid="maternal-snapshot">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Today's maternal focus</p>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight" data-testid="maternal-stage-label">
                {stage_info?.label || 'Set your stage'}
              </h2>
              {stage_info?.due_date && (
                <p className="text-xs text-gray-600 mt-0.5">Due ~ {new Date(stage_info.due_date).toLocaleDateString()}</p>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className={`inline-flex items-center gap-1.5 ${sp.text} text-sm font-bold`}>
                <span className={`w-2 h-2 rounded-full ${sp.dot}`} aria-hidden="true" />
                <span data-testid="maternal-status">{status}</span>
              </div>
              {last_checkin_at && (
                <p className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-1 justify-end">
                  <Clock className="h-3 w-3" />
                  Last check {relTime(last_checkin_at)}{typeof last_checkin_score === 'number' ? ` · ${last_checkin_score}/100` : ''}
                </p>
              )}
            </div>
          </div>

          {focus && focus.length > 0 && (
            <ul className="space-y-1.5 mb-3" data-testid="maternal-focus-list">
              {focus.map((f, i) => (
                <li key={f} className="text-sm text-gray-800 leading-relaxed flex items-start gap-2">
                  <span className="font-bold text-gray-500 shrink-0">{i + 1}.</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {reassurance && (
            <p className="text-xs text-gray-700 italic mb-3 leading-relaxed" data-testid="maternal-reassurance">{reassurance}</p>
          )}

          <button
            onClick={() => navigate('/maternal-health/checkin')}
            className={`w-full bg-gradient-to-br ${pal.from} ${pal.to} hover:brightness-110 text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all`}
            data-testid="maternal-checkin-cta"
          >
            Quick check-in →
          </button>
        </section>

        {/* WEEK / POSTPARTUM CARD */}
        {week_card && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4 hl-fade-up" data-testid="week-card">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-rose-600" />
              <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-widest">{week_card.title}</p>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed mb-2"><strong>Baby:</strong> {week_card.baby}</p>
            <p className="text-sm text-gray-800 leading-relaxed mb-2"><strong>You may feel:</strong> {week_card.mother}</p>
            <p className="text-sm text-gray-800 leading-relaxed mb-2"><strong>Focus this week:</strong> {week_card.focus}</p>
            <p className="text-xs text-gray-600 leading-relaxed"><strong>Nutrition:</strong> {week_card.nutrition}</p>
          </section>
        )}

        {postpartum_card && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4 hl-fade-up" data-testid="postpartum-card">
            <div className="flex items-center gap-2 mb-2">
              <Baby className="h-4 w-4 text-purple-600" />
              <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-widest">{postpartum_card.title}</p>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed mb-2">{postpartum_card.body}</p>
            <p className="text-sm text-gray-800 leading-relaxed mb-2"><strong>Focus:</strong> {postpartum_card.focus}</p>
            <p className="text-xs text-rose-700 leading-relaxed inline-flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{postpartum_card.warning}</span>
            </p>
          </section>
        )}

        {/* NEXT APPOINTMENT */}
        {next_appointment && (
          <button
            onClick={() => navigate('/maternal-health/appointments')}
            className="w-full mb-4 rounded-2xl bg-white border-2 border-gray-100 hover:border-rose-300 p-4 text-left flex items-center gap-3 transition-all hl-press hl-fade-up"
            data-testid="next-appointment"
          >
            <span className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
              <Calendar className="h-5 w-5 text-rose-700" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold text-gray-900">{next_appointment.title}</span>
              <span className="block text-xs text-gray-600 mt-0.5">{new Date(next_appointment.date).toLocaleDateString()}</span>
            </span>
            <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
          </button>
        )}

        {/* MODULES */}
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3 px-1">Quick actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {modules.map((m) => {
            const Mi = m.icon;
            const meta = moduleMeta(m.key);
            return (
              <button
                key={m.key}
                onClick={() => navigate(m.route)}
                className="text-left rounded-2xl bg-white border-2 border-gray-100 hover:border-rose-300 p-4 motion-safe:transition-colors hl-press"
                data-testid={`maternal-module-${m.key}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                    <Mi className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-base font-bold text-gray-900 leading-tight">{m.title}</h3>
                      {meta && (
                        <span
                          className={[
                            'inline-flex items-center text-[10px] font-medium leading-none rounded-full px-2 py-1 shrink-0 whitespace-nowrap',
                            meta.tone === 'fresh'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                              : 'bg-rose-50/70 text-rose-700 border border-rose-100',
                          ].join(' ')}
                          data-testid={`maternal-module-${m.key}-meta`}
                        >
                          {meta.tone === 'fresh' && (
                            <span className="w-1 h-1 rounded-full bg-emerald-500 mr-1" aria-hidden="true" />
                          )}
                          {meta.label}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{m.desc}</p>
                  </div>
                </div>
                <p className="text-xs font-semibold text-rose-700 inline-flex items-center gap-1 mt-1.5">
                  {m.cta} →
                </p>
              </button>
            );
          })}
        </div>

        <p className="text-[11px] text-gray-400 text-center mt-6">
          HealthLoop supports awareness — it does not diagnose or replace maternity care.
        </p>
      </div>
    </div>
  );
};

export default MaternalHub;
